// src/screens/GameStoreScreen.js
// ============================================================================
// GAME STORE SCREEN
//
// Displays the CritCash game catalog with:
// - Featured games section (CritCash Originals)
// - Search functionality
// - Category/genre filtering
// - Price filtering (free/paid)
// - Desktop client awareness
// - Navigation to GameDetailScreen
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  SafeAreaView,
  Modal,
  Platform,
  Linking,
  TextInput,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import {
  getFirestore, collection, query, where, getDocs, orderBy,
} from 'firebase/firestore';
import { auth, functions } from '../../firebaseConfig';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import GameStoreCard from '../components/GameStoreCard';
import { isDesktopApp, getPlatformInfo } from '../utils/platform';

const db = getFirestore();

// Category display config (using Ionicons names instead of emojis)
const CATEGORIES = {
  all: { label: 'All Games', iconName: 'game-controller', iconColor: '#FCD000' },
  first_party: { label: 'CritCash Originals', iconName: 'star', iconColor: '#FCD000' },
  partner: { label: 'Partner Games', iconName: 'people', iconColor: '#3498db' },
  supported: { label: 'Supported', iconName: 'tv', iconColor: '#9146FF' },
};

// Price filter options
const PRICE_FILTERS = {
  all: 'All Prices',
  free: 'Free to Play',
  paid: 'Paid',
};

export default function GameStoreScreen({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [userLibrary, setUserLibrary] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDesktopPrompt, setShowDesktopPrompt] = useState(false);
  const [platformInfo, setPlatformInfo] = useState(null);
  const { showError } = useError();

  const inDesktopClient = isDesktopApp();

  // Load store data on mount
  useEffect(() => {
    loadStoreData();
    
    if (inDesktopClient) {
      loadPlatformInfo();
    }
  }, []);

  const loadPlatformInfo = async () => {
    const info = await getPlatformInfo();
    setPlatformInfo(info);
  };

  const loadStoreData = async () => {
    setLoading(true);
    try {
      let gamesList = [];

      // Try cloud function first
      try {
        const getFeaturedGames = httpsCallable(functions, 'getFeaturedGames');
        const gamesResult = await getFeaturedGames();
        gamesList = gamesResult.data.games || [];
      } catch (fnErr) {
        console.log('[GameStore] getFeaturedGames unavailable, trying Firestore:', fnErr.message);
      }

      // Firestore fallback - read game_catalog directly
      if (gamesList.length === 0) {
        try {
          const q = query(collection(db, 'game_catalog'), where('status', '==', 'active'));
          const snap = await getDocs(q);
          gamesList = snap.docs.map(d => ({ id: d.id, gameId: d.id, ...d.data() }));
          gamesList.sort((a, b) => (b.featuredOrder || 0) - (a.featuredOrder || 0));
        } catch (fsErr) {
          console.log('[GameStore] Firestore fallback failed:', fsErr.message);
          // Try without where clause
          try {
            const snap = await getDocs(collection(db, 'game_catalog'));
            gamesList = snap.docs.map(d => ({ id: d.id, gameId: d.id, ...d.data() }));
            gamesList.sort((a, b) => (b.featuredOrder || 0) - (a.featuredOrder || 0));
          } catch (rawErr) {
            console.log('[GameStore] Raw Firestore read also failed:', rawErr.message);
          }
        }
      }

      setGames(gamesList);

      // Load user's library if logged in
      if (auth.currentUser) {
        try {
          const getLibrary = httpsCallable(functions, 'getGameLibrary');
          const libraryResult = await getLibrary();
          const libraryMap = {};
          (libraryResult.data.games || []).forEach(game => {
            libraryMap[game.gameId] = game;
          });
          setUserLibrary(libraryMap);
        } catch (libErr) {
          console.log('[GameStore] getLibrary unavailable, trying Firestore:', libErr.message);
          // Firestore fallback for library
          try {
            const libSnap = await getDocs(
              collection(db, 'users', auth.currentUser.uid, 'game_library')
            );
            const libraryMap = {};
            libSnap.docs.forEach(d => {
              const data = d.data();
              libraryMap[data.gameId || d.id] = data;
            });
            setUserLibrary(libraryMap);
          } catch (fsLibErr) {
            console.log('[GameStore] Firestore library fallback failed:', fsLibErr.message);
          }
        }
      }
    } catch (err) {
      console.error('[GameStore] Failed to load store:', err);
      showError('Failed to load game store');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding game to library
  const handleAddToLibrary = async (gameId) => {
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }

    try {
      const addToLibrary = httpsCallable(functions, 'addGameToLibrary');
      await addToLibrary({ gameId });
    } catch (fnErr) {
      console.log('[GameStore] addToLibrary function failed:', fnErr.message);
    }
      
    // Refresh library (try function, then Firestore fallback)
    try {
      const getLibrary = httpsCallable(functions, 'getGameLibrary');
      const libraryResult = await getLibrary();
      const libraryMap = {};
      (libraryResult.data.games || []).forEach(game => {
        libraryMap[game.gameId] = game;
      });
      setUserLibrary(libraryMap);
    } catch (libErr) {
      console.log('[GameStore] Library refresh failed, trying Firestore:', libErr.message);
      try {
        const libSnap = await getDocs(
          collection(db, 'users', auth.currentUser.uid, 'game_library')
        );
        const libraryMap = {};
        libSnap.docs.forEach(d => {
          const data = d.data();
          libraryMap[data.gameId || d.id] = data;
        });
        setUserLibrary(libraryMap);
      } catch (fsErr) {
        console.log('[GameStore] Firestore library fallback failed:', fsErr.message);
        throw fsErr;
      }
    }
  };

  // Navigate to game detail
  const handleGamePress = (gameId) => {
    navigation.navigate('GameDetail', { gameId });
  };

  // Filter and search games
  const filteredGames = useMemo(() => {
    let result = [...games];

    // Apply category filter
    if (selectedCategory !== 'all') {
      result = result.filter(g => g.category === selectedCategory);
    }

    // Apply price filter
    if (priceFilter === 'free') {
      result = result.filter(g => !g.priceCents || g.priceCents === 0);
    } else if (priceFilter === 'paid') {
      result = result.filter(g => g.priceCents && g.priceCents > 0);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(g => 
        g.name?.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query) ||
        g.genres?.some(genre => genre.toLowerCase().includes(query)) ||
        g.developer?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [games, selectedCategory, priceFilter, searchQuery]);

  // Separate first-party games for featured section (only when no filters)
  const featuredGames = useMemo(() => {
    if (selectedCategory !== 'all' || searchQuery.trim() || priceFilter !== 'all') {
      return [];
    }
    return games.filter(g => g.category === 'first_party');
  }, [games, selectedCategory, searchQuery, priceFilter]);

  // Other games (non-featured)
  const otherGames = useMemo(() => {
    if (selectedCategory !== 'all' || searchQuery.trim() || priceFilter !== 'all') {
      return filteredGames;
    }
    return filteredGames.filter(g => g.category !== 'first_party');
  }, [filteredGames, selectedCategory, searchQuery, priceFilter]);

  // Handle desktop app download
  const handleGetDesktopApp = () => {
    navigation.navigate('Download');
    setShowDesktopPrompt(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory('all');
    setPriceFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedCategory !== 'all' || priceFilter !== 'all' || searchQuery.trim();

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading store...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Page Title */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageTitle}><Ionicons name="game-controller" size={22} color="#FCD000" /> Game Store</Text>
          {inDesktopClient && platformInfo && (
            <View style={styles.desktopBadge}>
              <Text style={styles.desktopBadgeText}>
                Desktop v{platformInfo.version}
              </Text>
            </View>
          )}
        </View>

        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={18} color="#666" style={{ marginRight: 6 }} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search games, genres, developers..."
              placeholderTextColor="#555"
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={18} color="#999" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Desktop Client Prompt (for web users) */}
        {!inDesktopClient && (
          <TouchableOpacity 
            style={styles.desktopPromptBanner}
            onPress={() => setShowDesktopPrompt(true)}
          >
            <View style={styles.promptIcon}>
              <Ionicons name="desktop-outline" size={24} color="#FCD000" />
            </View>
            <View style={styles.promptContent}>
              <Text style={styles.promptTitle}>Get the Desktop App</Text>
              <Text style={styles.promptSubtitle}>
                Download and play games directly on your PC
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#666" />
          </TouchableOpacity>
        )}

        {/* Filter Row */}
        <View style={styles.filterSection}>
          {/* Category Tabs */}
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.categoryTabs}
          >
            {Object.entries(CATEGORIES).map(([key, { label, iconName, iconColor }]) => (
              <TouchableOpacity
                key={key}
                style={[
                  styles.categoryTab,
                  selectedCategory === key && styles.categoryTabActive,
                ]}
                onPress={() => setSelectedCategory(key)}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Ionicons name={iconName} size={14} color={selectedCategory === key ? '#FCD000' : '#999'} style={{ marginRight: 4 }} />
                  <Text style={[
                    styles.categoryTabText,
                    selectedCategory === key && styles.categoryTabTextActive,
                  ]}>
                    {label}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Price Filter */}
          <View style={styles.priceFilterRow}>
            {Object.entries(PRICE_FILTERS).map(([key, label]) => (
              <TouchableOpacity
                key={key}
                style={[
                  styles.priceTab,
                  priceFilter === key && styles.priceTabActive,
                ]}
                onPress={() => setPriceFilter(key)}
              >
                <Text style={[
                  styles.priceTabText,
                  priceFilter === key && styles.priceTabTextActive,
                ]}>
                  {label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Active Filters Banner */}
        {hasActiveFilters && (
          <View style={styles.activeFiltersBar}>
            <Text style={styles.resultsCount}>
              {filteredGames.length} {filteredGames.length === 1 ? 'game' : 'games'} found
            </Text>
            <TouchableOpacity onPress={clearFilters}>
              <Text style={styles.clearFiltersText}>Clear Filters</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Featured Section (CritCash Originals) */}
        {featuredGames.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}><Ionicons name="star" size={16} color="#FCD000" /> CritCash Originals</Text>
            <Text style={styles.sectionSubtitle}>
              Built-in tournaments, no streaming required
            </Text>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.horizontalList}
            >
              {featuredGames.map((game) => (
                <TouchableOpacity
                  key={game.gameId}
                  onPress={() => handleGamePress(game.gameId)}
                  activeOpacity={0.8}
                >
                  <GameStoreCard
                    game={game}
                    libraryEntry={userLibrary[game.gameId]}
                    onAddToLibrary={handleAddToLibrary}
                    onShowDesktopPrompt={() => setShowDesktopPrompt(true)}
                  />
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* All Games Grid */}
        <View style={styles.section}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {hasActiveFilters 
              ? <Ionicons name="search" size={16} color="#FCD000" style={{ marginRight: 6 }} />
              : selectedCategory === 'all' 
                ? <Ionicons name="game-controller" size={16} color="#FCD000" style={{ marginRight: 6 }} />
                : <Ionicons name={CATEGORIES[selectedCategory].iconName} size={16} color={CATEGORIES[selectedCategory].iconColor} style={{ marginRight: 6 }} />
            }
            <Text style={styles.sectionTitle}>
              {hasActiveFilters 
                ? 'Search Results' 
                : selectedCategory === 'all' 
                  ? 'All Games' 
                  : CATEGORIES[selectedCategory].label
              }
            </Text>
          </View>
          
          {otherGames.length === 0 ? (
            <View style={styles.emptyState}>
              {searchQuery.trim() 
                ? <Ionicons name="search" size={40} color="#666" />
                : <Ionicons name="mail-open-outline" size={40} color="#666" />
              }
              <Text style={styles.emptyTitle}>
                {searchQuery.trim() 
                  ? 'No games match your search' 
                  : 'No games in this category yet'
                }
              </Text>
              {hasActiveFilters && (
                <TouchableOpacity style={styles.clearButton} onPress={clearFilters}>
                  <Text style={styles.clearButtonText}>Clear Filters</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <View style={styles.gamesGrid}>
              {otherGames.map((game) => (
                <TouchableOpacity
                  key={game.gameId}
                  onPress={() => handleGamePress(game.gameId)}
                  activeOpacity={0.8}
                >
                  <GameStoreCard
                    game={game}
                    libraryEntry={userLibrary[game.gameId]}
                    onAddToLibrary={handleAddToLibrary}
                    onShowDesktopPrompt={() => setShowDesktopPrompt(true)}
                  />
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Library Quick Access (if user has games) */}
        {Object.keys(userLibrary).length > 0 && !hasActiveFilters && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}><Ionicons name="library-outline" size={16} color="#FCD000" /> Your Library</Text>
              <TouchableOpacity onPress={() => navigation.navigate('GameLibrary')}>
                <Text style={styles.viewAllLink}>View All <Ionicons name="chevron-forward" size={12} color="#FCD000" /></Text>
              </TouchableOpacity>
            </View>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.horizontalList}
            >
              {Object.values(userLibrary).slice(0, 5).map((entry) => {
                const game = games.find(g => g.gameId === entry.gameId) || entry;
                return (
                  <TouchableOpacity
                    key={entry.gameId}
                    onPress={() => handleGamePress(entry.gameId)}
                    activeOpacity={0.8}
                  >
                    <GameStoreCard
                      game={game}
                      libraryEntry={entry}
                      onAddToLibrary={handleAddToLibrary}
                      onShowDesktopPrompt={() => setShowDesktopPrompt(true)}
                    />
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        )}
      </ScrollView>

      {/* Desktop App Modal */}
      <Modal
        visible={showDesktopPrompt}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDesktopPrompt(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Ionicons name="desktop-outline" size={48} color="#FCD000" />
            <Text style={styles.modalTitle}>CritCash Desktop</Text>
            <Text style={styles.modalText}>
              Download and play games directly on your PC. The desktop app handles downloads, 
              updates, and launching games seamlessly.
            </Text>

            <View style={styles.modalFeatures}>
              <View style={styles.featureItem}>
                <Ionicons name="download-outline" size={20} color="#FCD000" />
                <Text style={styles.featureText}>Download games with one click</Text>
              </View>
              <View style={styles.featureItem}>
                <Ionicons name="rocket-outline" size={20} color="#FCD000" />
                <Text style={styles.featureText}>One-click game launching</Text>
              </View>
              <View style={styles.featureItem}>
                <Ionicons name="refresh-outline" size={20} color="#FCD000" />
                <Text style={styles.featureText}>Automatic updates</Text>
              </View>
              <View style={styles.featureItem}>
                <Ionicons name="trophy-outline" size={20} color="#FCD000" />
                <Text style={styles.featureText}>Built-in tournament integration</Text>
              </View>
            </View>

            <TouchableOpacity
              style={styles.downloadButton}
              onPress={handleGetDesktopApp}
            >
              <Text style={styles.downloadButtonText}>Download for Free</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowDesktopPrompt(false)}
            >
              <Text style={styles.closeButtonText}>Maybe Later</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0b0b0b',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 12,
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Page Header
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#FCD000',
  },
  desktopBadge: {
    backgroundColor: '#1a3a1a',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#22c55e',
  },
  desktopBadgeText: {
    fontSize: 12,
    color: '#22c55e',
    fontWeight: '500',
  },

  // Search
  searchContainer: {
    marginBottom: 12,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#151515',
    borderRadius: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#242424',
  },
  searchIcon: {
    fontSize: 16,
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 14,
    color: '#fff',
  },
  clearSearch: {
    fontSize: 16,
    color: '#555',
    padding: 4,
  },

  // Desktop Prompt Banner
  desktopPromptBanner: {
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#2563eb',
  },
  promptIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#2563eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  promptIconText: {
    fontSize: 22,
  },
  promptContent: {
    flex: 1,
  },
  promptTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 2,
  },
  promptSubtitle: {
    fontSize: 12,
    color: '#888',
  },
  promptArrow: {
    fontSize: 18,
    color: '#2563eb',
    marginLeft: 8,
  },

  // Filters
  filterSection: {
    marginBottom: 12,
  },
  categoryTabs: {
    flexDirection: 'row',
    marginBottom: 10,
    gap: 6,
    paddingRight: 12,
  },
  categoryTab: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#151515',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  categoryTabActive: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  categoryTabText: {
    fontSize: 12,
    color: '#ddd',
    fontWeight: '800',
  },
  categoryTabTextActive: {
    color: '#000',
  },
  priceFilterRow: {
    flexDirection: 'row',
    gap: 6,
  },
  priceTab: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 10,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  priceTabActive: {
    backgroundColor: '#222',
    borderColor: '#444',
  },
  priceTabText: {
    fontSize: 11,
    color: '#666',
    fontWeight: '800',
  },
  priceTabTextActive: {
    color: '#fff',
  },

  // Active Filters
  activeFiltersBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 8,
  },
  resultsCount: {
    fontSize: 12,
    color: '#888',
  },
  clearFiltersText: {
    fontSize: 12,
    color: '#FCD000',
    fontWeight: '600',
  },

  // Sections
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '900',
    color: '#fff',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 12,
  },
  viewAllLink: {
    fontSize: 13,
    color: '#FCD000',
    fontWeight: '600',
  },
  horizontalList: {
    paddingRight: 12,
  },
  gamesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    padding: 40,
  },
  emptyEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 14,
    color: '#888',
    marginBottom: 16,
    textAlign: 'center',
  },
  clearButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: '#222',
    borderRadius: 8,
  },
  clearButtonText: {
    fontSize: 13,
    color: '#fff',
    fontWeight: '600',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    padding: 28,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#333',
  },
  modalEmoji: {
    fontSize: 56,
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  modalFeatures: {
    width: '100%',
    marginBottom: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureIcon: {
    fontSize: 18,
    marginRight: 12,
    width: 28,
  },
  featureText: {
    fontSize: 14,
    color: '#ccc',
  },
  downloadButton: {
    backgroundColor: '#2563eb',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  downloadButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  closeButton: {
    paddingVertical: 10,
  },
  closeButtonText: {
    fontSize: 14,
    color: '#666',
  },
});